import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface RsvpKey {
  eventId: string;
  occurrenceDate: string; // YYYY-MM-DD
}

interface UseRsvpResult {
  count: number;
  isGoing: boolean;
  loading: boolean;
  toggling: boolean;
  toggle: () => Promise<void>;
}

/**
 * Manages RSVP state for a single event occurrence.
 * - Reads count via the public RPC (no auth needed).
 * - Reads "going" state from event_rsvps for the current member.
 * - Subscribes to realtime updates so counts stay live.
 */
export const useEventRsvp = (
  { eventId, occurrenceDate }: RsvpKey,
  memberId: string | null
): UseRsvpResult => {
  const [count, setCount] = useState(0);
  const [isGoing, setIsGoing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const fetchState = useCallback(async () => {
    try {
      const { data: countRows } = await supabase.rpc("get_event_rsvp_counts", {
        _event_ids: [eventId],
      });
      const row = (countRows || []).find(
        (r: any) => r.occurrence_date === occurrenceDate
      );
      setCount(row ? Number(row.rsvp_count) : 0);

      if (memberId) {
        const { data: mine } = await supabase
          .from("event_rsvps")
          .select("id")
          .eq("event_id", eventId)
          .eq("occurrence_date", occurrenceDate)
          .eq("member_id", memberId)
          .maybeSingle();
        setIsGoing(!!mine);
      } else {
        setIsGoing(false);
      }
    } catch (err) {
      console.error("Error loading RSVP state:", err);
    } finally {
      setLoading(false);
    }
  }, [eventId, occurrenceDate, memberId]);

  useEffect(() => {
    fetchState();

    const channel = supabase
      .channel(`event-rsvps-${eventId}-${occurrenceDate}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "event_rsvps",
          filter: `event_id=eq.${eventId}`,
        },
        () => fetchState()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, occurrenceDate, fetchState]);

  const toggle = async () => {
    if (!memberId || toggling) return;
    setToggling(true);
    try {
      if (isGoing) {
        await supabase
          .from("event_rsvps")
          .delete()
          .eq("event_id", eventId)
          .eq("occurrence_date", occurrenceDate)
          .eq("member_id", memberId);
      } else {
        await supabase.from("event_rsvps").insert({
          event_id: eventId,
          occurrence_date: occurrenceDate,
          member_id: memberId,
        });
      }
      await fetchState();
    } finally {
      setToggling(false);
    }
  };

  return { count, isGoing, loading, toggling, toggle };
};

/**
 * Looks up the current user's member_id (or null if not logged in / not a member).
 */
export const useCurrentMemberId = () => {
  const [memberId, setMemberId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setMemberId(null);
        setLoading(false);
        return;
      }
      const { data } = await supabase.rpc("get_member_id_for_user", {
        _user_id: session.user.id,
      });
      setMemberId((data as string) || null);
      setLoading(false);
    };
    load();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => load());
    return () => subscription.unsubscribe();
  }, []);

  return { memberId, loading };
};
